import Vue from "vue";
import Vuex from "vuex";
import { apiClient, allowAnonymousClient } from "../axios/index.js";
import { UrlAPI } from "../enums/index.js";

const { get, post, put, putFormData } = apiClient;

Vue.use(Vuex);

const state = {
  token: "",

  session: {
    UserId: "",
    UserName: "",
    mail: "",
    exp: null,
  },

  permissions: [],

  role: "",

  graph: {
    mixinItUpdates: true,
    mixinStartDate: null,
    mixinEndDate: null,
    mixinShowFilter: false,
  },
};

//mutaciones
const mutations = {
  modifySession(state, { userId, username, mail, exp }) {
    state.session.UserId = userId;
    state.session.UserName = username;
    state.session.mail = mail;
    state.session.exp = exp;
  },

  modifyPermissions(state, permissions) {
    // If permissions is a string, split by comma
    if (typeof permissions === 'string') {
      state.permissions = permissions.split(',').map(p => p.trim()).filter(p => p);
    } else if (Array.isArray(permissions)) {
      state.permissions = permissions;
    } else {
      // If it's neither a string nor an array, default to empty array
      state.permissions = [];
    }
  },

  modifyRole(state, role) {
    state.role = role;
  },

  resetUserState(state) {
    state.session.UserId = "";
    state.session.UserName = "";
    state.session.mail = "";
    state.session.exp = null;
    state.permissions = [];
    state.role = "";
  },

  modifyGraph(state, graph) {
    state.graph.mixinItUpdates = graph.mixinItUpdates;
    state.graph.mixinStartDate = graph.mixinStartDate;
    state.graph.mixinEndDate = graph.mixinEndDate;
    state.graph.mixinShowFilter = graph.mixinShowFilter;
  },
};

//gets
const getters = {
  getSession: (state) => {
    return state.session;
  },

  getPermissions: (state) => {
    return state.permissions;
  },

  getRole: (state) => {
    return state.role;
  },

  getGraph: (state) => {
    return state.graph;
  },

  getHomePath: (state, getters) => {
    const hasDashboardPermission =
      getters.getPermissions.includes("dashboard-vista");
    return hasDashboardPermission ? "Home" : "Board";
  },
};

//acciones
const actions = {
  async login(context, data) {
    return await post(UrlAPI.loginApi, data);
  },

  async logout(context, data) {
    return await post(UrlAPI.logoutApi, data);
  },

  async checkOtp(context, data) {
    return await post(UrlAPI.checkOtpApi, data);
  },

  async forgottenPassword(context, email) {
    return await post(
      `${UrlAPI.forgottenPasswordApi}?mail=${encodeURIComponent(email)}`
    );
  },

  async allUsersForPage(context, data) {
    return await get(UrlAPI.allUsersForPage, data);
  },

  async countUsers(context, data) {
    return await get(UrlAPI.countUsers, data);
  },

  async allRoles(context, data) {
    return await get(UrlAPI.allRoles, data);
  },

  async createUser(context, data) {
    return await put(UrlAPI.createUser, data);
  },

  async modifyUser(context, data) {
    return await post(UrlAPI.modifyUser, data);
  },

  async deleteUser(context, data) {
    return await post(UrlAPI.deleteUser, data);
  },

  async allDocumentType(context, data) {
    return await get(UrlAPI.allDocumentType, data);
  },

  async allDestination(context, data) {
    return await get(UrlAPI.allDestination, data);
  },

  async uploadFileDocument(context, data) {
    return await putFormData(UrlAPI.uploadFileDocument, data);
  },

  async saveDocument(context, data) {
    return await put(UrlAPI.saveDocument, data);
  },

  async allDocument(context, data) {
    return await get(UrlAPI.allDocument, data);
  },

  async getDocumentType(context, data) {
    return await get(UrlAPI.getDocumentType, data);
  },

  async countDocument(context, data) {
    return await get(UrlAPI.countDocument, data);
  },

  async getUrl(context, data) {
    return await get(UrlAPI.getUrl, data);
  },

  async createViewDocument(context, data) {
    return await put(UrlAPI.createViewDocument, data);
  },

  async getViewDocument(context, data) {
    return await get(UrlAPI.getViewDocument, data);
  },

  async getPermissions(context, data) {
    return await get(UrlAPI.getViewPermission, data);
  },

  async getUserById(context, data) {
    return await get(UrlAPI.getUserById, data);
  },

  async removePermissionForRole(context, data) {
    return await post(UrlAPI.removePermissionForRole, data);
  },

  async deleteDocument(context, data) {
    return await post(UrlAPI.deleteDocument, data);
  },

  async getAllVisitorForDocumentId(context, data) {
    return await get(UrlAPI.getAllVisitorForDocumentId, data);
  },

  async getAllNonVisitorForDocumentId(context, data) {
    return await get(UrlAPI.getAllNonVisitorForDocumentId, data);
  },

  async allDocumentByName(context, data) {
    return await get(UrlAPI.allDocumentByName, data);
  },

  async allDocumentByType(context, data) {
    return await get(UrlAPI.allDocumentByType, data);
  },

  async allDocumentByDate(context, data) {
    return await get(UrlAPI.allDocumentByDate, data);
  },

  async getNonPermissionForRole(context, data) {
    return await get(UrlAPI.getNonPermissionForRole, data);
  },

  async addPermissionFromRole(context, data) {
    return await post(UrlAPI.addPermissionFromRole, data);
  },

  async saveDocumentType(context, data) {
    return await put(UrlAPI.saveDocumentType, data);
  },

  async saveRole(context, data) {
    return await put(UrlAPI.saveRole, data);
  },

  async getAllQuestions(context, data) {
    return await allowAnonymousClient.get(UrlAPI.getAllQuestions, data);
  },

  async getAccusersByMail(context, data) {
    return await allowAnonymousClient.get(UrlAPI.getAccusersByMail, data);
  },

  async createAccusersAnonymous(context, data) {
    return await allowAnonymousClient.put(UrlAPI.createAccusersExternal, data);
  },

  async updateFileDenunciation(context, data) {
    return await allowAnonymousClient.putFormData(
      UrlAPI.updateFileDenunciation,
      data
    );
  },

  async createDenunciation(context, data) {
    return await allowAnonymousClient.put(UrlAPI.createDenunciation, data);
  },

  async allDenunciationsByPage(context, data) {
    return await get(UrlAPI.getAllDenunciationsByPage, data);
  },

  async allDenunciationsByDate(context, data) {
    return await get(UrlAPI.getAllDenunciationsByDate, data);
  },

  async getEvidenciaUrl(context, data) {
    return await get(UrlAPI.getEvidenciaUrl, data);
  },

  async countDenunciations(context, data) {
    return await get(UrlAPI.countDenunciations, data);
  },

  async createQuestion(context, data) {
    return await put(UrlAPI.createQuestion, data);
  },

  async allCommentsDenounce(context, data) {
    return await get(UrlAPI.allCommentsDenounce, data);
  },

  async addCommentDenounce(context, data) {
    return await post(UrlAPI.addCommentDenounce, data);
  },

  async addDenounceResolution(context, data) {
    return await post(UrlAPI.addDenounceResolution, data);
  },

  async allDocumentNotification(context, data) {
    return await get(UrlAPI.allDocumentNotification, data);
  },

  async countSendDocument(context, data) {
    return await get(UrlAPI.countSendDocument, data);
  },

  async allDenounceNotification(context, data) {
    return await get(UrlAPI.allDenounceNotification, data);
  },

  async countSendDenounce(context, data) {
    return await get(UrlAPI.countSendDenounce, data);
  },

  async getAllDocumentByDate(context, data) {
    return await get(UrlAPI.getAllDocumentByDate, data);
  },

  async getAllTerritories(context, data) {
    return await get(UrlAPI.getAllTerritories, data);
  },

  async updateCompany(context, data) {
    return await post(UrlAPI.updateCompany, data);
  },

  async getCompany(context, data) {
    return await get(UrlAPI.getCompany, data);
  },

  async saveStatementTemplate(context, data) {
    return await post(UrlAPI.saveStatementTemplate, data);
  },

  async saveStatementDocument(context, data) {
    return await putFormData(UrlAPI.saveStatementDocument, data);
  },

  async getStatementDocument(context, data) {
    return await get(UrlAPI.getStatementDocument, data);
  },

  async saveStatementQuestion(context, data) {
    return await put(UrlAPI.saveStatementQuestion, data);
  },

  async getAllStatementTemplates(context, data) {
    return await get(UrlAPI.getAllStatementTemplates, data);
  },

  async getAllStatementTemplatesForUser(context, data) {
    return await get(UrlAPI.getAllStatementTemplatesForUser, data);
  },

  async getTemplateDocumentUrl(context, data) {
    return await get(UrlAPI.getTemplateDocumentUrl, data);
  },

  async getAllStatementQuestions(context, data) {
    return await get(UrlAPI.getAllStatementQuestions, data);
  },

  async saveStatement(context, data) {
    return await post(UrlAPI.saveStatement, data);
  },

  async getAllStatementsByPage(context, data) {
    return await get(UrlAPI.getAllStatementsByPage, data);
  },

  async countStatements(context, data) {
    return await get(UrlAPI.countStatements, data);
  },

  async getAllStatementsByDate(context, data) {
    return await get(UrlAPI.getAllStatementsByDate, data);
  },

  // PEP Statements Actions
  async getAllPepStatementsByPage(context, data) {
    return await get(UrlAPI.getAllPepStatementsByPage, data);
  },

  async countPepStatements(context, data) {
    return await get(UrlAPI.countPepStatements, data);
  },

  async getAllPepStatementsByDate(context, data) {
    return await get(UrlAPI.getAllPepStatementsByDate, data);
  },
  async savePepStatement(context, data) {
    return await post(UrlAPI.savePepStatement, data);
  },
  async getPepTemplateDocumentUrl(context, data) {
    return await get(UrlAPI.getPepTemplateDocumentUrl, data);
  },
  async getAllPepStatementTemplatesForUser(context, data) {
    return await get(UrlAPI.getAllPepStatementTemplatesForUser, data);
  },
  async getAllPepStatementQuestions(context, data) {
    return await get(UrlAPI.getAllPepStatementQuestions, data);
  },

  async savePepStatementTemplate(context, data) {
    return await post(UrlAPI.savePepStatementTemplate, data);
  },

  async getAllPepStatementTemplates(context, data) {
    return await get(UrlAPI.getAllPepStatementTemplates, data);
  },

  async getAllPepQuestionChildren(context, data) {
    return await get(UrlAPI.getAllPepQuestionChildren, data);
  },

  async uploadPepDocumentFile(context, data) {
    return await putFormData(UrlAPI.uploadPepDocumentFile, data);
  },

  async getPepDocumentUrl(context, data) {
    return await get(UrlAPI.getPepDocumentUrl, data);
  },

  async countPepStatementTemplateNotifications(context, data) {
    return await get(UrlAPI.countPepStatementTemplateNotifications, data);
  },

  async getAllPepStatementTemplateNotificationsByPage(context, data) {
    return await get(
      UrlAPI.getAllPepStatementTemplateNotificationsByPage,
      data
    );
  },

  async getAllPepSendStatementsByPage(context, data) {
    return await get(UrlAPI.getAllPepSendStatementsByPage, data);
  },

  async countPepSendStatement(context, data) {
    return await get(UrlAPI.countPepSendStatement, data);
  },

  // Family Statements Actions
  async getAllFamilyStatementsByPage(context, data) {
    return await get(UrlAPI.getAllFamilyStatementsByPage, data);
  },

  async countFamilyStatements(context, data) {
    return await get(UrlAPI.countFamilyStatements, data);
  },

  async getAllFamilyStatementsByDate(context, data) {
    return await get(UrlAPI.getAllFamilyStatementsByDate, data);
  },

  async saveFamilyStatement(context, data) {
    return await post(UrlAPI.saveFamilyStatement, data);
  },

  async saveFamilyStatementTemplate(context, data) {
    return await post(UrlAPI.saveFamilyStatementTemplate, data);
  },

  async getAllFamilyStatementTemplates(context, data) {
    return await get(UrlAPI.getAllFamilyStatementTemplates, data);
  },

  async getAllFamilyStatementTemplatesForUser(context, data) {
    return await get(UrlAPI.getAllFamilyStatementTemplatesForUser, data);
  },

  async getAllFamilyStatementQuestions(context, data) {
    return await get(UrlAPI.getAllFamilyStatementQuestions, data);
  },

  async getAllFamilyQuestionChildren(context, data) {
    return await get(UrlAPI.getAllFamilyQuestionChildren, data);
  },

  async uploadFamilyDocumentFile(context, data) {
    return await putFormData(UrlAPI.uploadFamilyDocumentFile, data);
  },

  async getFamilyDocumentUrl(context, data) {
    return await get(UrlAPI.getFamilyDocumentUrl, data);
  },

  async getFamilyTemplateDocumentUrl(context, data) {
    return await get(UrlAPI.getFamilyTemplateDocumentUrl, data);
  },

  async countFamilyStatementTemplateNotifications(context, data) {
    return await get(UrlAPI.countFamilyStatementTemplateNotifications, data);
  },

  async getAllFamilyStatementTemplateNotificationsByPage(context, data) {
    return await get(
      UrlAPI.getAllFamilyStatementTemplateNotificationsByPage,
      data
    );
  },

  async countFamilySendStatement(context, data) {
    return await get(UrlAPI.countFamilySendStatement, data);
  },

  async getAllFamilySendStatementsByPage(context, data) {
    return await get(UrlAPI.getAllFamilySendStatementsByPage, data);
  },

  // Conflicts Of Interest Actions
  async getAllConflictsOfInterest(context, data) {
    return await get(UrlAPI.getAllConflictsOfInterest, data);
  },

  async countConflictsOfInterest(context, data) {
    return await get(UrlAPI.countConflictsOfInterest, data);
  },

  async updateCommentConflictOfInterest(context, data) {
    return await put(UrlAPI.updateCommentConflictOfInterest, data);
  },

  async getAllCommentsConflictOfInterest(context, data) {
    return await get(UrlAPI.getAllCommentsConflictOfInterest, data);
  },

  async countSendStatement(context, data) {
    return await get(UrlAPI.countSendStatement, data);
  },

  async getAllSendStatementsByPage(context, data) {
    return await get(UrlAPI.getAllSendStatementsByPage, data);
  },

  async getTemplateNotificationsCount(context, data) {
    return await get(UrlAPI.getTemplateNotificationsCount, data);
  },

  async getAllTemplateNotifications(context, data) {
    return await get(UrlAPI.getAllTemplateNotifications, data);
  },

  async changePassword(context, data) {
    return await put(UrlAPI.ChangePassword, data);
  },

  async createCompany(context, data) {
    return await post(UrlAPI.CreateCompany, data);
  },

  async createSubject(context, data) {
    return await post(UrlAPI.CreateSubject, data);
  },

  async updateCompanyDocument(context, data) {
    return await putFormData(UrlAPI.updateCompanyDocument, data);
  },

  async getAllEntities(context, data) {
    return await get(UrlAPI.getAllEntities, data);
  },

  async addShareholder(context, data) {
    return await post(UrlAPI.addShareholder, data);
  },

  async deleteShareholder(context, data) {
    return await post(UrlAPI.deleteShareholder, data);
  },

  async getShareholderStructure(context, data) {
    return await get(UrlAPI.getShareholderStructure, data);
  },

  async editShareholder(context, data) {
    return await post(UrlAPI.EditCompany, data);
  },

  async editSubject(context, data) {
    return await post(UrlAPI.EditSubject, data);
  },

  async getAllDocumentHistory(context, data) {
    return await get(UrlAPI.getAllDocumentHistory, data);
  },

  async getShareholderPercentage(context, data) {
    return await get(UrlAPI.getShareholderPercentage, data);
  },

  async setShareholderPercentage(context, data) {
    return await post(UrlAPI.setShareholderPercentage, data);
  },

  async updateCompanyInstanceDocument(context, data) {
    return await putFormData(UrlAPI.updateCompanyInstanceDocument, data);
  },

  async saveCompanyInstanceDocument(context, data) {
    return await post(UrlAPI.saveCompanyInstanceDocument, data);
  },

  async allMeetingsForPage(context, data) {
    return await get(UrlAPI.allMeetingsForPage, data);
  },

  async countMeetings(context, data) {
    return await get(UrlAPI.countMeetings, data);
  },

  async getAllMeetingsByDate(context, data) {
    return await get(UrlAPI.getAllMeetingsByDate, data);
  },

  async getAllMeetingsByRole(context, data) {
    return await get(UrlAPI.getAllMeetingsByRole, data);
  },
  async createMeetingVirtual(context, data) {
    return await post(UrlAPI.createMeetingVirtual, data);
  },

  async createMeetingPhysical(context, data) {
    return await post(UrlAPI.createMeetingPhysical, data);
  },

  async allCommentsMeeting(context, data) {
    return await get(UrlAPI.allCommentsMeeting, data);
  },

  async addCommentMeeting(context, data) {
    return await post(UrlAPI.addCommentMeeting, data);
  },

  async allMeetingDocumentation(context, data) {
    return await get(UrlAPI.allMeetingDocumentation, data);
  },

  async addMeetingDocumentation(context, data) {
    return await post(UrlAPI.addMeetingDocumentation, data);
  },

  async getMeetingDocumentationUrl(context, data) {
    return await get(UrlAPI.getMeetingDocumentationUrl, data);
  },

  async uploadMeetingDocumentation(context, data) {
    return await putFormData(UrlAPI.uploadMeetingDocumentation, data);
  },

  async getAllMails(context, data) {
    return await get(UrlAPI.getAllMails, data);
  },

  async getKarinDenunciationTypes(context, data) {
    return await allowAnonymousClient.get(
      UrlAPI.getKarinDenunciationTypes,
      data
    );
  },

  async getKarinAccuserByMail(context, data) {
    return await allowAnonymousClient.get(UrlAPI.getKarinAccuserByMail, data);
  },

  async uploadFileKarinDenunciation(context, data) {
    return await allowAnonymousClient.putFormData(
      UrlAPI.uploadFileKarinDenunciation,
      data
    );
  },

  async createKarinDenunciation(context, data) {
    return await allowAnonymousClient.post(
      UrlAPI.createKarinDenunciation,
      data
    );
  },

  async createKarinAccusersAnonymous(context, data) {
    return await allowAnonymousClient.put(
      UrlAPI.createKarinAccusersAnonymous,
      data
    );
  },

  async allKarinDenunciaion(context, data) {
    return await get(UrlAPI.allKarinDenunciation, data);
  },

  async allKarinDenunciationByDate(context, data) {
    return await get(UrlAPI.allKarinDenunciationByDate, data);
  },

  async countKarinDenunciation(context, data) {
    return await get(UrlAPI.countKarinDenunciation, data);
  },

  async addCommentKarinDenunciation(context, data) {
    return await post(UrlAPI.addCommentKarinDenunciation, data);
  },

  async allCommentsKarinDenunciation(context, data) {
    return await get(UrlAPI.allCommentsKarinDenunciation, data);
  },

  async createKarinDenunciationType(context, data) {
    return await post(UrlAPI.createKarinDenunciationType, data);
  },

  async getKarinDocumentUrl(context, data) {
    return await get(UrlAPI.getKarinDocumentUrl, data);
  },

  async addDocumentKarinDenunciation(context, data) {
    return await allowAnonymousClient.post(
      UrlAPI.addDocumentKarinDenunciation,
      data
    );
  },

  async addKarinDenunciationResolution(context, data) {
    return await post(UrlAPI.addKarinDenunciationResolution, data);
  },

  async countKarinNotification(context, data) {
    return await get(UrlAPI.countKarinNotification, data);
  },

  async allKarinNotification(context, data) {
    return await get(UrlAPI.allKarinNotification, data);
  },

  async saveCourse(context, data) {
    return await post(UrlAPI.saveCourse, data);
  },

  async countCourses(context, data) {
    return await get(UrlAPI.countCourses, data);
  },

  async allCoursesForPage(context, data) {
    return await get(UrlAPI.allCoursesForPage, data);
  },

  async deleteCourse(context, data) {
    return await post(UrlAPI.deleteCourse, data);
  },

  async deleteCurriculum(context, data) {
    return await post(UrlAPI.deleteCurriculum, data);
  },

  async saveTheme(context, data) {
    return await post(UrlAPI.saveTheme, data);
  },

  async saveThemeResourceDocument(context, data) {
    return await putFormData(UrlAPI.saveThemeResourceDocument, data);
  },

  async getResourceUrl(context, data) {
    return await get(UrlAPI.getResourceUrl, data);
  },

  async addCurriculumResource(context, data) {
    return await post(UrlAPI.addCurriculumResource, data);
  },

  async getEvaluationById(context, data) {
    return await get(UrlAPI.getEvaluationById, data);
  },

  async addEvaluation(context, data) {
    return await post(UrlAPI.addEvaluation, data);
  },

  async getCourseById(context, data) {
    return await get(UrlAPI.getCourseById, data);
  },

  async addRolesToCourse(context, data) {
    return await post(UrlAPI.addRolesToCourse, data);
  },

  async deleteEvaluation(context, data) {
    return await post(UrlAPI.deleteEvaluation, data);
  },

  async deleteResource(context, data) {
    return await post(UrlAPI.deleteResource, data);
  },

  async sendEvaluation(context, data) {
    return await post(UrlAPI.sendEvaluation, data);
  },

  async allQualificationsForPage(context, data) {
    return await get(UrlAPI.allQualificationsForPage, data);
  },

  async allEvaluationAttemptsForPage(context, data) {
    return await get(UrlAPI.allEvaluationAttemptsForPage, data);
  },

  async countEvaluationAttempts(context, data) {
    return await get(UrlAPI.countEvaluationAttempts, data);
  },

  async getEvaluationSummary(context, data) {
    return await get(UrlAPI.getEvaluationSummary, data);
  },

  async getEvaluationApproval(context, data) {
    return await get(UrlAPI.getEvaluationApproval, data);
  },
};

export const Store = new Vuex.Store({
  state: state,
  mutations: mutations,
  actions: actions,
  getters: getters,
});
